import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { checksForDestination, destinationLabel, isStreamUnhealthy } from './streamHealth'
import { useAuditLogStreamingStatus, useStatusQueryKey } from './useAuditLogStreamingStatus'

import styles from './AuditLogStreamingSection.module.scss'

import { setupAuditLogStreaming, updateAuditLogStreamingRetention } from '@/api/auditLogStreaming'
import { AuditLogStreamState } from '@/api/generated/api'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import FormErrorBanner from '@/components/FormErrorBanner'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'

const RETENTION_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '365', label: '1 year' },
]

const STATE_COPY: Record<AuditLogStreamState, { label: string; className?: string }> = {
  [AuditLogStreamState.Active]: { label: 'Active', className: styles.statusActive },
  [AuditLogStreamState.Inactive]: { label: 'Paused' },
  [AuditLogStreamState.Error]: { label: 'Delivery failing', className: styles.statusError },
  [AuditLogStreamState.Invalid]: { label: 'Configuration problem', className: styles.statusError },
}

const AuditLogStreamingSection = () => {
  const { updateToast } = useToast()
  const queryClient = useQueryClient()
  const [pendingRetention, setPendingRetention] = useState<30 | 365 | null>(null)
  const statusQueryKey = useStatusQueryKey()
  const statusQuery = useAuditLogStreamingStatus()

  const setupMutation = useMutation({
    mutationFn: async ({ placeholder }: { placeholder: Window | null }) => {
      try {
        const res = await setupAuditLogStreaming()
        return { portalUrl: res.data.portal_url, placeholder }
      } catch (err) {
        placeholder?.close()
        throw err
      }
    },
    onSuccess: ({ portalUrl, placeholder }) => {
      // Popup blockers only treat window.open as user-initiated inside the click handler;
      // by onSuccess (post-await) most browsers would silently block it. The click handler
      // opens a placeholder up front; here we just point it at the real URL.
      if (placeholder && !placeholder.closed) {
        placeholder.location.href = portalUrl
      } else {
        window.open(portalUrl, '_blank', 'noopener,noreferrer')
      }
    },
    onError: () => {
      updateToast('Failed to open the streaming setup page. Please try again.', 'error', 5000)
    },
  })

  const retentionMutation = useMutation({
    mutationFn: (retentionPeriodInDays: 30 | 365) => updateAuditLogStreamingRetention(retentionPeriodInDays),
    onSuccess: (res) => {
      queryClient.setQueryData(statusQueryKey, res.data)
      updateToast('Retention updated.', 'success', 3000)
    },
    onError: () => {
      updateToast('Failed to update retention. Please try again.', 'error', 5000)
    },
  })

  const handleConfigureClick = () => {
    // Open synchronously, inside the click event, so the browser counts it as
    // user-initiated before the setup call goes async to mint the portal URL.
    const placeholder = window.open('about:blank', '_blank')
    setupMutation.mutate({ placeholder })
  }

  const handleRetentionChange = (value: string) => {
    const next = Number(value) as 30 | 365
    const current = statusQuery.data?.retention_period_in_days
    if (current === 365 && next === 30) {
      setPendingRetention(next)
      return
    }
    retentionMutation.mutate(next)
  }

  if (statusQuery.isLoading) {
    return (
      <div className={styles.section}>
        <span className={styles.status}>Loading streaming status…</span>
      </div>
    )
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <div className={styles.section}>
        <FormErrorBanner message="Couldn't load audit log streaming status." />
      </div>
    )
  }

  const { log_stream: logStream, retention_period_in_days: retentionPeriodInDays } = statusQuery.data
  const stateCopy = logStream ? STATE_COPY[logStream.state] : null

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <span className={styles.title}>Audit log streaming</span>
        <p className={styles.description}>
          Stream your enterprise&apos;s audit events in real time to your SIEM (Security Information and Event
          Management) destination via WorkOS.
        </p>
        {logStream ? (
          <span className={`${styles.status} ${stateCopy?.className ?? ''}`}>
            {stateCopy?.label} — streaming to {logStream.type}
          </span>
        ) : (
          <span className={styles.status}>Not configured</span>
        )}
        {logStream && isStreamUnhealthy(logStream) && (
          <div className={styles.problem} role="status">
            <p className={styles.problemTitle}>Events aren&apos;t reaching {destinationLabel(logStream.type)}.</p>
            <ul className={styles.problemChecks}>
              {checksForDestination(logStream.type).map((check) => (
                <li key={check}>{check}</li>
              ))}
            </ul>
            <p className={styles.problemNote}>
              After fixing it, choose Manage streaming and save the stream again. Delivery resumes once it works.
            </p>
            {logStream.error_message && (
              <details className={styles.problemDetails}>
                <summary>Technical details</summary>
                <pre>{logStream.error_message}</pre>
              </details>
            )}
          </div>
        )}
        {logStream && !isStreamUnhealthy(logStream) && (
          <span className={styles.status}>
            {logStream.last_synced_at
              ? `Last event delivered ${new Date(logStream.last_synced_at).toLocaleString()}`
              : 'No events delivered yet.'}
          </span>
        )}
      </div>

      <div className={styles.retentionRow} role="group" aria-labelledby="audit-log-streaming-retention-label">
        <span id="audit-log-streaming-retention-label" className={styles.retentionLabel}>
          Retention
        </span>
        <Select
          id="audit-log-streaming-retention"
          value={String(retentionPeriodInDays)}
          options={RETENTION_OPTIONS}
          isDisabled={retentionMutation.isPending}
          onChange={handleRetentionChange}
        />
      </div>

      <div className={styles.footer}>
        <Button
          title={logStream ? 'Manage streaming' : 'Configure streaming'}
          type="dark"
          onClick={handleConfigureClick}
          isLoading={setupMutation.isPending}
        />
      </div>

      <ConfirmDialog
        isOpen={pendingRetention !== null}
        title="Shorten audit log retention?"
        message="This will reduce retention from 1 year to 30 days. Events older than 30 days will no longer be retained."
        confirmLabel="Yes, shorten retention"
        confirmType="destructive"
        isConfirming={retentionMutation.isPending}
        onConfirm={() => {
          if (pendingRetention !== null) retentionMutation.mutate(pendingRetention)
          setPendingRetention(null)
        }}
        onCancel={() => setPendingRetention(null)}
      />
    </div>
  )
}

export default AuditLogStreamingSection
